import React from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import classNames from 'classnames';
import { AccessComponentEnum, AccessLevelEnum } from 'graphql-common';
import WhiteBox from '@lib/components/WhiteBox/WhiteBox';
import DashboardContentMaxWidth from '@lib/layouts/DashboardLayout/DashboardContentMaxWidth/DashboardContentMaxWidth';
import DashboardTitleHeader from '@lib/layouts/DashboardLayout/DashboardTitleHeader/DashboardTitleHeader';
import CircleLoaderWrapper from '@lib/components/CircleLoaderWrapper/CircleLoaderWrapper';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import Tooltip from '@lib/components/Tooltip/Tooltip';
import Trans from '@lib/components/Trans/Trans';
import ConfirmationModal from '@lib/components/ConfirmationModal/ConfirmationModal';
import MaterialIcon from '@lib/components/MaterialIcon/MaterialIcon';
import Typography from '@lib/components/Typography/Typography';
import TextEditor from '@lib/components/TextEditor/TextEditor';
import { hasTextEditorValue } from '@lib/components/TextEditor/utils';
import UserChip from '@lib/components/UserChip/UserChip';
import useModalManagement, { Modals } from '@lib/hooks/useModalManagement';
import { IconName } from '@lib/components/Modal/enums';
import { materialSymbolsOutlined } from '@lib/enums/common';
import { getFormattedDate } from '@lib/utils/dateTimeHelpers';
import ViewOnlyChip from '@lib/components/ViewOnlyChip/ViewOnlyChip';
import entityViewStyles from '@lib/assets/styles/EntityView.module.scss';
import useOutletContext, { ContextProps } from 'hooks/useOutletContext';
import DepartmentHierarchyView from '../components/DepartmentHierarchyView/DepartmentHierarchyView';
import useDepartmentViewActions from '../utils/useDepartmentViewActions';
import { UseDepartmentQueriesResult } from '../utils/useDepartmentQueries';
import styles from './DepartmentViewDetails.module.scss';

const modals = [Modals.DepartmentDestroyModal];

type Props = {
  useDepartmentQueriesResult: UseDepartmentQueriesResult;
};

export default function DepartmentViewDetails({
  useDepartmentQueriesResult,
}: Props) {
  const { t } = useTranslation();
  const { id = '' } = useParams();
  const { useDepartmentQueryHookResult } = useDepartmentQueriesResult;
  const { data, firstLoading } = useDepartmentQueryHookResult;
  const { name, description, createdAt, manager, canDestroy } = data || {};
  const isDestroyAvailable = canDestroy?.value;

  const { getUserAccessByComponent }: ContextProps = useOutletContext();
  const isViewOnly =
    getUserAccessByComponent(AccessComponentEnum.DepartmentsManagement) ===
    AccessLevelEnum.Read;

  const useModalManagementResult = useModalManagement(modals);
  const { closeModal, isModalOpened } = useModalManagementResult;
  const { onEdit, onDelete, onConfirmedDelete, destroyLoading } =
    useDepartmentViewActions({ useModalManagementResult });

  return (
    <DashboardContentMaxWidth>
      <CircleLoaderWrapper
        isLoading={firstLoading}
        rootClassName={styles.loaderRoot}
      >
        <WhiteBox>
          <DashboardTitleHeader
            title={name}
            actions={
              isViewOnly ? (
                <ViewOnlyChip key="view" />
              ) : (
                <Button
                  key="edit"
                  buttonText={t('edit')}
                  buttonType={ButtonTypes.primaryOutlined}
                  onClick={onEdit}
                  leftIcon="edit"
                />
              )
            }
          />
          <div className={entityViewStyles.infoList}>
            <div className={entityViewStyles.infoItem}>
              <p className={entityViewStyles.infoItemLabel}>
                <MaterialIcon icon="calendar_today" symbolsOutlined />
                <Typography variant="caption">{t('date-created')}</Typography>
              </p>
              <div className={entityViewStyles.infoItemContent}>
                <Typography variant="body">
                  {getFormattedDate(createdAt)}
                </Typography>
              </div>
            </div>
            <div className={entityViewStyles.infoItem}>
              <p className={entityViewStyles.infoItemLabel}>
                <MaterialIcon icon="person" symbolsOutlined />
                <Typography variant="caption">
                  {t('responsible-manager')}
                </Typography>
              </p>
              <div className={entityViewStyles.infoItemContent}>
                {manager ? (
                  <UserChip user={manager} />
                ) : (
                  <Typography variant="body">-</Typography>
                )}
              </div>
            </div>
            {hasTextEditorValue(description) && (
              <div
                className={classNames(
                  entityViewStyles.infoItem,
                  entityViewStyles.block,
                )}
              >
                <p className={entityViewStyles.infoItemLabel}>
                  <MaterialIcon icon="description" symbolsOutlined />
                  <Typography variant="caption">{t('description')}</Typography>
                </p>
                <div className={entityViewStyles.infoItemContent}>
                  <TextEditor
                    readOnly
                    value={description}
                    className={entityViewStyles.textEditor}
                  />
                </div>
              </div>
            )}
          </div>
          {id && <DepartmentHierarchyView departmentId={id} />}
        </WhiteBox>
        <div className={entityViewStyles.actions}>
          {isDestroyAvailable ? (
            <Button
              buttonText={t('delete-department')}
              buttonType={ButtonTypes.negativeOutlined}
              leftIcon="delete"
              leftIconClassName={materialSymbolsOutlined}
              onClick={onDelete}
            />
          ) : (
            <Tooltip body={t('department-disabled-destroy-desc')}>
              <Button
                buttonText={t('delete-department')}
                buttonType={ButtonTypes.negativeOutlined}
                leftIcon="delete"
                leftIconClassName={materialSymbolsOutlined}
                disabled
              />
            </Tooltip>
          )}
        </div>
      </CircleLoaderWrapper>
      <ConfirmationModal
        confirmButtonLabel={t('yes-delete')}
        description={<Trans i18nKey="delete-department-text" />}
        icon={IconName.Delete}
        isOpen={isModalOpened(Modals.DepartmentDestroyModal)}
        loading={destroyLoading}
        onClose={() => closeModal(Modals.DepartmentDestroyModal)}
        onConfirm={onConfirmedDelete}
        title={t('are-you-sure')}
        useProgress
      />
    </DashboardContentMaxWidth>
  );
}
